import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import {
  injectInfiniteQuery,
  injectMutation,
  injectQueryClient,
} from '@tanstack/angular-query-experimental';
import { MessageService } from 'primeng/api';
import { lastValueFrom, map } from 'rxjs';
import {
  CreateWorkflowDefinitionRequest,
  CreateWorkflowDefinitionResponse,
  DeleteService,
  SearchWorkflowItem,
  SearchWorkflowRequest,
  WorkflowService,
} from 'workflow-definition/openapi';
import { TWO_MINUTES_IN_MILLISECONDS } from '../../constants/time-definitions';
import { StudioCapabilitiesService } from '../studio-capabilities/studio-capabilities.service';

/** Global templates — not scoped to any customer. */
export const isStandardTemplate = (template: SearchWorkflowItem): boolean =>
  template.customerId === '' || template.customerId === null;

/** Templates owned by a specific customer. */
export const isOwnTemplate = (template: SearchWorkflowItem): boolean =>
  !!(template.customerId && template.customerId !== '');

@Injectable({
  providedIn: 'root',
})
export class MyWorkflowsService {
  public queryClient = injectQueryClient();

  constructor(
    private workflowService: WorkflowService,
    private translate: TranslateService,
    private router: Router,
    private deleteService: DeleteService,
    private messageService: MessageService,
    private capabilities: StudioCapabilitiesService
  ) {}

  public duplicateWorkflow() {
    return injectMutation(() => ({
      mutationFn: ({
        workflow,
        toTemplate,
      }: {
        workflow: SearchWorkflowItem;
        toTemplate: boolean;
      }) =>
        lastValueFrom(
          this.workflowService.apiV1WorkflowDuplicateWorkflowIdPost(
            Number(workflow.id),
            {
              division: workflow.division,
              customerId: workflow.customerId,
              toTemplate: toTemplate,
            }
          )
        ),
      onSuccess: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Workflow duplicated successfully.',
        });
      },
      onError: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message,
        });
      },
      onSettled: () => {
        this.queryClient.invalidateQueries({ queryKey: ['WorkflowTemplates'] });
        this.queryClient.invalidateQueries({ queryKey: ['Workflows'] });
      },
    }));
  }

  public createWorkflow() {
    return injectMutation(() => ({
      mutationFn: (payload: CreateWorkflowDefinitionRequest) =>
        lastValueFrom(this.workflowService.apiV1WorkflowCreatePost(payload)),
      onSuccess: data => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: this.translate.instant('success-messages.create-workflow'),
        });
        setTimeout(() => this.navigate(data), 900);
      },
      onError: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message,
        });
      },
    }));
  }

  /**
   * Global and own workflow templates each come from their own independently-paginated
   * request (`onlyGlobal: true`/`false`), so neither can bury the other past a shared page
   * size the way a single combined, unpaginated search used to.
   */
  public globalWorkflowTemplatesQuery() {
    return injectInfiniteQuery(() => ({
      queryKey: ['WorkflowTemplates', 'global'],
      queryFn: async ({ pageParam }) =>
        (await lastValueFrom(
          this.workflowService
            .apiV1WorkflowSearchPost(pageParam)
            .pipe(map(res => res.results))
        )) ?? [],
      initialPageParam: {
        pageNum: 1,
        pageSize: 30,
        filterBy: { isTemplate: true, onlyGlobal: true },
      } as SearchWorkflowRequest,
      getNextPageParam: (last, _all, lastParam) =>
        (last?.length ?? 0) < (lastParam.pageSize ?? 30)
          ? undefined
          : { ...lastParam, pageNum: (lastParam.pageNum ?? 1) + 1 },
      staleTime: TWO_MINUTES_IN_MILLISECONDS,
    }));
  }

  public ownWorkflowTemplatesQuery() {
    return injectInfiniteQuery(() => ({
      queryKey: ['WorkflowTemplates', 'own'],
      enabled: this.capabilities.showOwnTemplatesList(),
      queryFn: async ({ pageParam }) =>
        (await lastValueFrom(
          this.workflowService
            .apiV1WorkflowSearchPost(pageParam)
            .pipe(map(res => res.results))
        )) ?? [],
      initialPageParam: {
        pageNum: 1,
        pageSize: 30,
        filterBy: { isTemplate: true, onlyGlobal: false },
      } as SearchWorkflowRequest,
      getNextPageParam: (last, _all, lastParam) =>
        (last?.length ?? 0) < (lastParam.pageSize ?? 30)
          ? undefined
          : { ...lastParam, pageNum: (lastParam.pageNum ?? 1) + 1 },
      staleTime: TWO_MINUTES_IN_MILLISECONDS,
    }));
  }

  public deleteWorkflow() {
    return injectMutation(() => ({
      mutationFn: (workflowId: number) =>
        lastValueFrom(
          this.deleteService.apiV1WorkflowWorkflowIdDelete(workflowId, {
            isRecursive: true,
          })
        ),
      onSuccess: () => {
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: 'Workflow deleted successfully.',
        });
      },
      onError: error => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: error.message,
        });
      },
      onSettled: () => {
        this.queryClient.invalidateQueries({ queryKey: ['WorkflowTemplates'] });
        this.queryClient.invalidateQueries({ queryKey: ['Workflows'] });
      },
    }));
  }

  navigate({ workflowId }: CreateWorkflowDefinitionResponse) {
    // Entry tab depends on the session: a global admin has no Fleets tab.
    this.router.navigateByUrl(
      `/workflow/${workflowId}/setup/${this.capabilities.setupEntryRoute()}`
    );
  }
}
